import { useState, useEffect, useCallback } from "react";
import { useAppContext } from "../context/AppContext";
import { getOrderSummary } from "../util/helper";

export default function useOrderManagement() {
  const { orderList, setOrderList, setCurrentOrderList } = useAppContext();
  const [isOrderSubmitted, setIsOrderSubmitted] = useState(false);

  useEffect(() => {
    if (orderList?.items?.length > 0) {
      const isSubmittedOrder = orderList.items.filter(
        (item) => item.isOrderSubmitted
      );
      setIsOrderSubmitted(isSubmittedOrder.length === orderList.items.length);
    } else {
      setIsOrderSubmitted(false);
    }
  }, [orderList]);

  const updateOrderLists = useCallback(
    (updater) => {
      setOrderList((prev) => {
        const newItems = updater(prev.items);
        return {
          ...prev,
          items: newItems,
          order_summary: getOrderSummary(newItems, prev.order_summary),
        };
      });
      setCurrentOrderList((prev) => {
        const newItems = updater(prev.items);
        return {
          ...prev,
          items: newItems,
          order_summary: getOrderSummary(newItems, prev.order_summary),
        };
      });
    },
    [setOrderList, setCurrentOrderList]
  );

  const handleDeleteItem = useCallback(
    (item) => {
      updateOrderLists((items) => items.filter((data) => data.id !== item.id));
    },
    [updateOrderLists]
  );

  /** Changes a cart line's quantity by `delta`; at zero the line is removed. */
  const handleChangeQuantity = useCallback(
    (item, delta) => {
      updateOrderLists((items) =>
        items.flatMap((line) => {
          if (line.id !== item.id) return [line];
          const quantity = (Number(line.quantity) || 1) + delta;
          if (quantity <= 0) return [];
          const unit =
            Number(line.unit_price) ||
            (Number(line.price) || 0) / (Number(line.quantity) || 1);
          return [{ ...line, quantity, unit_price: unit, price: unit * quantity }];
        })
      );
    },
    [updateOrderLists]
  );

  const handleSubmit = useCallback(
    (
      onClose,
      items,
      {
        cookingLevel = [],
        removeIngredients = [],
        notes = "",
        setNotes = () => {},
        setCookingLevel = () => {},
        setRemoveIngredients = () => {},
        setSelectedItem = () => {},
      } = {}
    ) => {
      const data = {
        ...items,
        cookingLevel,
        removeIngredients: removeIngredients || [],
        notes,
        isOrderSubmitted: false,
        name: items?.name || "",
      };
      if (items.edit && items.id) {
        updateOrderLists((orderItems) =>
          orderItems.map((orderItem) =>
            orderItem.id === items.id
              ? { ...orderItem, ...data, edit: undefined }
              : orderItem
          )
        );
      } else {
        updateOrderLists((orderItems = []) => [...orderItems, data]);
      }
      setNotes("");
      setCookingLevel([]);
      setRemoveIngredients([]);
      setSelectedItem(null);
      if (onClose) onClose();
    },
    [updateOrderLists]
  );

  return {
    orderList,
    setOrderList,
    setCurrentOrderList,
    isOrderSubmitted,
    handleDeleteItem,
    handleChangeQuantity,
    handleSubmit,
  };
}
